/**
 * Operations · Batches.
 */
import { useMemo, useState } from "react";
import { FlaskConical, Sparkles } from "lucide-react";
import { Card, SectionHeader, TextInput, Button } from "../../components/lib";
import { DocumentActions } from "../../components/DocumentActions";
import { TabBar, ListRow, Drawer, Field, Segmented } from "../../components/lib.overlay";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { AuditTrail } from "../../components/AuditTrail";
import { useDeepLink, useAutoOpen, RelatedLinksBlock } from "../../lib/integration";
import { linksForBatch } from "../../mocks/entityLinks";
import { BATCHES, BatchStage, Batch, QC_CHECKS, SUPPLIERS, MATERIALS, FULFILLMENTS } from "../../mocks/operationsData";
import { FORMULAS } from "../../mocks/rndData";

const STAGE_LABEL: Record<BatchStage, string> = {
  macerating: "Macerating", blending: "Blending", filtering: "Filtering",
  bottling: "Bottling", cured: "Cured", done: "Done", hold: "On hold",
};
const STAGE_TINT: Record<BatchStage, { bg: string; fg: string }> = {
  macerating: { bg: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  blending:   { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  filtering:  { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  bottling:   { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  cured:      { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  done:       { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"      },
  hold:       { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"        },
};
// Defensive fallback for any unexpected stage value coming from live data.
// Prevents the whole page from error-boundary'ing on a single unknown row.
const STAGE_FALLBACK = { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)" };
const stageTint = (s: string) => (STAGE_TINT as Record<string, { bg: string; fg: string }>)[s] || STAGE_FALLBACK;
const stageLabel = (s: string) => (STAGE_LABEL as Record<string, string>)[s] || s;

export default function Batches() {
  const { q: initialQ, open, clearOpen } = useDeepLink();
  const [tab, setTab] = useState<BatchStage | "all">("all");
  const [q, setQ]     = useState(initialQ);
  const [selected, setSelected] = useState<Batch | null>(null);
  const [wsTab, setWsTab] = useState<"overview" | "formula" | "qc" | "cost" | "packaging" | "docs" | "audit">("overview");
  const [editorOpen, setEditorOpen] = useState<"create" | "edit" | null>(null);
  useAutoOpen(open, BATCHES, (b) => b.id, setSelected);

  const BATCH_SCHEMA: FieldSpec[] = [
    { key: "code",           label: "Batch code",    kind: "text",   placeholder: "B-N001-25" },
    { key: "sku",            label: "SKU",           kind: "text",   placeholder: "ORV-NA-050" },
    { key: "productName",    label: "Product",       kind: "text",   placeholder: "Nocturne Amber" },
    { key: "units",          label: "Units",         kind: "number", placeholder: "500" },
    { key: "stage",          label: "Stage",         kind: "select", options: [
        { value: "macerating", label: "Macerating" }, { value: "blending",  label: "Blending"  },
        { value: "filtering",  label: "Filtering"  }, { value: "bottling",  label: "Bottling"  },
        { value: "cured",      label: "Cured"      }, { value: "done",      label: "Done"      },
        { value: "hold",       label: "On hold"    },
    ]},
    { key: "masterPerfumer", label: "Master perfumer", kind: "text", placeholder: "Y. Halim" },
    { key: "startedRelative",label: "Started",       kind: "text",   placeholder: "3 days ago" },
    { key: "etaRelative",    label: "ETA",           kind: "text",   placeholder: "in 4 days" },
    { key: "yieldPct",       label: "Yield %",       kind: "number", placeholder: "0.94" },
    { key: "qcScore",        label: "QC score",      kind: "number", placeholder: "92" },
    { key: "aiNote",         label: "AI note",       kind: "textarea", placeholder: "Optional" },
  ];

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: BATCHES.length };
    BATCHES.forEach((b) => { c[b.stage] = (c[b.stage] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => BATCHES.filter((b) =>
      (tab === "all" || b.stage === tab) &&
      (!q || b.code.toLowerCase().includes(q.toLowerCase()) || b.productName.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  return (
    <div className="space-y-6" data-testid="op-batches">
      <Card testId="op-batches-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search batch code or product…" testId="op-batches-search" /></div>
          <Button testId="op-batches-new">+ New batch</Button>
        </div>
        <div className="mt-4">
          <TabBar<BatchStage | "all">
            testId="op-batches-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",        label: "All",        count: counts.all        },
              { key: "macerating", label: "Macerating", count: counts.macerating || 0 },
              { key: "blending",   label: "Blending",   count: counts.blending   || 0 },
              { key: "filtering",  label: "Filtering",  count: counts.filtering  || 0 },
              { key: "bottling",   label: "Bottling",   count: counts.bottling   || 0 },
              { key: "cured",      label: "Cured",      count: counts.cured      || 0 },
              { key: "done",       label: "Done",       count: counts.done       || 0 },
              { key: "hold",       label: "On hold",    count: counts.hold       || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="op-batches-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Production floor" title={`${filtered.length} batch${filtered.length === 1 ? "" : "es"}`} action={<FlaskConical size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
          <DocumentActions docKey="batch-report" testId="batches-doc-actions" />
        </div>
        <div className="p-2" data-testid="op-batches-list">
          {filtered.map((b) => {
            const tint = stageTint(b.stage);
            return (
              <ListRow
                key={b.id}
                testId={`op-batch-${b.id}`}
                onClick={() => setSelected(b)}
                label={
                  <span className="inline-flex items-center gap-2">
                    <span className="font-mono text-[11px]" style={{ color: "var(--ph-ink-muted)" }}>{b.code}</span>
                    <span>{b.productName}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: tint.bg, color: tint.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {stageLabel(b.stage)}
                    </span>
                    {b.aiNote && (
                      <span className="inline-flex items-center gap-1 text-[10px]" style={{ color: "var(--ph-ai)" }}>
                        <Sparkles size={9} /> AI
                      </span>
                    )}
                  </span>
                }
                sublabel={<>{b.units} units · started {b.startedRelative} · ETA {b.etaRelative} · {b.masterPerfumer}</>}
                trailing={
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>{Math.round(b.yieldPct * 100)}%</div>
                    <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Yield</div>
                  </div>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected) && editorOpen === null} onClose={() => { setSelected(null); clearOpen(); }} title="Batch 360" testId="op-batch-drawer" width={620}>
        {selected && (() => {
          const linkedQC = QC_CHECKS.filter((qc) => qc.batchCode === selected.code);
          const formula = FORMULAS.find((f) => f.id.toLowerCase().includes(selected.productName.toLowerCase().split(" ")[0]));
          const rmSuppliers = SUPPLIERS.slice(0, 3);
          const rmList = MATERIALS.slice(0, 4);
          const shipments = FULFILLMENTS.filter((f) => (f.orderRef || "").includes(selected.code) || (f.customer || "").includes(selected.productName));
          const rmCost = 850, packagingCost = 240, laborCost = 120;
          const cogsPerUnit = rmCost + packagingCost + laborCost;
          const totalCost = cogsPerUnit * selected.units;
          return (
            <div>
              <div className="text-[10px] font-mono" style={{ color: "var(--ph-ink-muted)" }}>{selected.code}</div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">
                {selected.productName}
              </div>
              <div className="mb-3">
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: stageTint(selected.stage).bg, color: stageTint(selected.stage).fg, borderRadius: "var(--ph-radius-sm)" }}>
                  {stageLabel(selected.stage)}
                </span>
                <span className="text-xs ml-2" style={{ color: "var(--ph-ink-muted)" }}>{selected.units} units · Yield {Math.round(selected.yieldPct * 100)}%</span>
              </div>
              <div className="mb-3 pb-3 flex items-center gap-3 flex-wrap" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                <DocumentActions docKey="batch-report" recordId={selected.id} testId={`op-batch-single-${selected.id}`} compact />
                <DocumentActions docKey="qc-report"    recordId={selected.id} testId={`op-qc-single-${selected.id}`} compact />
              </div>
              <div className="mb-4 overflow-x-auto">
                <Segmented<"overview" | "formula" | "qc" | "cost" | "packaging" | "docs" | "audit">
                  testId="op-batch-ws-tabs"
                  value={wsTab}
                  onChange={setWsTab}
                  options={[
                    { value: "overview",  label: "Overview"  },
                    { value: "formula",   label: "Formula & RM" },
                    { value: "qc",        label: "QC"        },
                    { value: "cost",      label: "Cost"      },
                    { value: "packaging", label: "Packaging" },
                    { value: "docs",      label: "Docs"      },
                    { value: "audit",     label: "Audit"     },
                  ]}
                />
              </div>

              {wsTab === "overview" && (
                <div data-testid="op-batch-ws-overview">
                  <Field label="SKU"             inline><span className="text-sm font-mono">{selected.sku}</span></Field>
                  <Field label="Stage"           inline><span className="text-sm">{stageLabel(selected.stage)}</span></Field>
                  <Field label="Units"           inline><span className="text-sm tabular-nums">{selected.units}</span></Field>
                  <Field label="Yield"           inline><span className="text-sm tabular-nums">{Math.round(selected.yieldPct * 100)}%</span></Field>
                  <Field label="Master perfumer" inline><span className="text-sm">{selected.masterPerfumer}</span></Field>
                  <Field label="Started"         inline><span className="text-sm">{selected.startedRelative}</span></Field>
                  <Field label="ETA"             inline><span className="text-sm">{selected.etaRelative}</span></Field>
                  <Field label="Warehouse"       inline><span className="text-sm">Main lab · Bay {selected.id.slice(-1)}</span></Field>
                  {typeof selected.qcScore === "number" && <Field label="QC score" inline><span className="text-sm tabular-nums">{selected.qcScore}/100</span></Field>}
                  {selected.aiNote && (
                    <div className="pt-3">
                      <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ai)" }}>AI note</div>
                      <div className="text-sm p-3" style={{ background: "var(--ph-ai-tint)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                        {selected.aiNote}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {wsTab === "formula" && (
                <div data-testid="op-batch-ws-formula">
                  <Field label="Formula" inline><span className="text-sm">{formula ? `${formula.id} · ${formula.version}` : "—"}</span></Field>
                  <Field label="IFRA compliant" inline><span className="text-sm" style={{ color: formula?.ifraCompliant ? "var(--ph-success)" : "var(--ph-warning)" }}>{formula?.ifraCompliant ? "Yes" : "Review"}</span></Field>
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Raw materials · {rmList.length}</div>
                    {rmList.map((m) => (
                      <div key={m.id} data-testid={`op-batch-rm-${m.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                        <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{m.name}</div>
                        <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{m.onHand}{m.unit} on hand · {m.level}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Supplier list · {rmSuppliers.length}</div>
                    {rmSuppliers.map((s) => (
                      <div key={s.id} data-testid={`op-batch-sup-${s.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                        <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{s.name}</div>
                        <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>Risk {s.risk} · OTIF {s.onTimeRate}%</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {wsTab === "qc" && (
                <div data-testid="op-batch-ws-qc">
                  <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>QC timeline · {linkedQC.length}</div>
                  {linkedQC.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No QC checks recorded.</div>}
                  {linkedQC.map((qc) => (
                    <div key={qc.id} data-testid={`op-batch-qc-${qc.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                      <div className="text-sm font-medium capitalize" style={{ color: "var(--ph-ink-strong)" }}>{qc.checkType.replace("-", " ")}</div>
                      <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{qc.status} · {qc.result} · {qc.performedBy} · {qc.performedRelative}</div>
                    </div>
                  ))}
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Blind results</div>
                    <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                      Panel of 12 · avg score 8.4/10 · winning formula
                    </div>
                  </div>
                </div>
              )}

              {wsTab === "cost" && (
                <div data-testid="op-batch-ws-cost">
                  <Field label="RM cost / unit"        inline><span className="text-sm tabular-nums">EGP {rmCost}</span></Field>
                  <Field label="Packaging cost / unit" inline><span className="text-sm tabular-nums">EGP {packagingCost}</span></Field>
                  <Field label="Labor cost / unit"     inline><span className="text-sm tabular-nums">EGP {laborCost}</span></Field>
                  <Field label="COGS / unit"           inline><span className="text-sm font-semibold tabular-nums">EGP {cogsPerUnit}</span></Field>
                  <Field label="Total batch cost"      inline><span className="text-sm font-semibold tabular-nums">EGP {totalCost.toLocaleString()}</span></Field>
                </div>
              )}

              {wsTab === "packaging" && (
                <div data-testid="op-batch-ws-packaging">
                  <Field label="Packaging status" inline><span className="text-sm capitalize">{selected.stage === "done" ? "Packaged" : selected.stage === "bottling" ? "In progress" : "Pending"}</span></Field>
                  <Field label="Bottle"           inline><span className="text-sm">{selected.productName} · 50ml glass</span></Field>
                  <Field label="Cap"              inline><span className="text-sm">Brass polished</span></Field>
                  <Field label="Label"            inline><span className="text-sm">Premium foil</span></Field>
                  <Field label="Box"              inline><span className="text-sm">Standard</span></Field>
                  <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Shipments referencing this batch · {shipments.length}</div>
                    {shipments.length === 0 && <div className="text-sm py-2" style={{ color: "var(--ph-ink-muted)" }}>No shipments yet.</div>}
                    {shipments.map((f) => (
                      <div key={f.id} data-testid={`op-batch-ship-${f.id}`} className="py-2" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
                        <div className="text-sm font-medium" style={{ color: "var(--ph-ink-strong)" }}>{f.orderRef}</div>
                        <div className="text-xs" style={{ color: "var(--ph-ink-muted)" }}>{f.customer} · {f.stage}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {wsTab === "docs" && (
                <div data-testid="op-batch-ws-docs">
                  <RelatedLinksBlock links={linksForBatch(selected.id)} testId="op-batch-related" />
                </div>
              )}

              {wsTab === "audit" && (
                <div data-testid="op-batch-ws-audit">
                  <AuditTrail collection="batches" recordId={selected.id} testId="op-batch-audit" />
                </div>
              )}

              <div className="pt-4 flex justify-end gap-2">
                <Button variant="secondary" testId="op-batch-hold">Place on hold</Button>
                <Button testId="op-batch-advance">Advance stage</Button>
                <Button testId="op-batch-edit" onClick={() => setEditorOpen("edit")}>Edit</Button>
              </div>
            </div>
          );
        })()}
      </Drawer>

      <RecordEditor<Batch>
        open={editorOpen !== null}
        onClose={() => setEditorOpen(null)}
        mode={editorOpen === "edit" ? "edit" : "create"}
        title={editorOpen === "edit" ? "Edit batch" : "New batch"}
        collection="batches"
        schema={BATCH_SCHEMA}
        initial={editorOpen === "edit" && selected ? (selected as unknown as Record<string, unknown>) : {}}
        onSaved={(row) => { if (editorOpen === "edit") setSelected(row as unknown as Batch); }}
        testId="op-batch-editor"
      />
    </div>
  );
}
